import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem,
  FormControl, InputLabel, Select, Alert, CircularProgress, Chip,
  Grid, Divider, Paper, IconButton, Tooltip, Tab, Tabs, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, LinearProgress
} from '@mui/material';
import {
  Add, Restaurant, Schedule, CalendarMonth, Timeline as TimelineIcon,
  Refresh, ArrowBack, ArrowForward, AccessTime, LocalDining,
  TrendingUp, Info, Close, Today
} from '@mui/icons-material';
import { feedEventsAPI } from 'services/api';

// Feed Types with labels
const FEED_TYPES = [
  { value: 'starter', label: 'Starter Feed', description: 'For DOC 1-30' },
  { value: 'grower', label: 'Grower Feed', description: 'For DOC 31-60' },
  { value: 'finisher', label: 'Finisher Feed', description: 'For DOC 61+' },
  { value: 'supplement', label: 'Supplement', description: 'Nutritional additives' },
  { value: 'probiotic', label: 'Probiotic', description: 'Health supplements' },
  { value: 'medicated', label: 'Medicated', description: 'Treatment feeds' },
];

// Time Slots with hours
const TIME_SLOTS = [
  { value: 'morning', label: 'Morning', hours: '6:00 AM - 10:00 AM', icon: '🌅' },
  { value: 'afternoon', label: 'Afternoon', hours: '11:00 AM - 3:00 PM', icon: '☀️' },
  { value: 'evening', label: 'Evening', hours: '4:00 PM - 7:00 PM', icon: '🌆' },
  { value: 'night', label: 'Night', hours: '8:00 PM - 5:00 AM', icon: '🌙' },
];

const FeedManagement = ({ pondId, pondName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [feedEvents, setFeedEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Add Feed Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Calendar navigation
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  
  // Feed form
  const [formData, setFormData] = useState({
    feed_date_time: new Date().toISOString().slice(0, 16),
    quantity_kg: '',
    feed_type: 'grower',
    feed_size_mm: '',
    feeding_time_slot: 'morning',
    remarks: '',
    feed_brand: '',
    unit_cost: ''
  });

  useEffect(() => {
    if (pondId) {
      loadFeedData();
    }
  }, [pondId, tabValue, calendarMonth, calendarYear]);

  const loadFeedData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load based on active tab
      const [summaryRes, eventsRes] = await Promise.all([
        feedEventsAPI.getSummary(pondId),
        tabValue === 0 ? feedEventsAPI.getTimeline(pondId, 30) : null
      ]);
      
      setSummary(summaryRes.data);
      
      if (tabValue === 0) {
        setFeedEvents(eventsRes?.data || []);
      } else if (tabValue === 1) {
        // Load calendar data
        const calendarRes = await feedEventsAPI.getCalendar(pondId, calendarMonth, calendarYear);
        setCalendar(calendarRes.data);
      } else if (tabValue === 2) {
        // Load full timeline
        const timelineRes = await feedEventsAPI.getTimeline(pondId, 90);
        setFeedEvents(timelineRes.data || []);
      }
    } catch (err) {
      console.error('Error loading feed data:', err);
      setError('Failed to load feed data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeed = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      // Validate
      if (!formData.quantity_kg || parseFloat(formData.quantity_kg) <= 0) {
        setError('Please enter a valid quantity');
        return;
      }
      
      await feedEventsAPI.create(pondId, {
        pond_id: pondId,
        feed_date_time: formData.feed_date_time,
        quantity_kg: parseFloat(formData.quantity_kg),
        feed_type: formData.feed_type,
        feed_size_mm: formData.feed_size_mm ? parseFloat(formData.feed_size_mm) : null,
        feeding_time_slot: formData.feeding_time_slot,
        remarks: formData.remarks || null,
        feed_brand: formData.feed_brand || null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null
      });
      
      setSuccess('Feed entry added successfully');
      setAddDialogOpen(false);
      
      // Reset form
      setFormData({
        feed_date_time: new Date().toISOString().slice(0, 16),
        quantity_kg: '',
        feed_type: 'grower',
        feed_size_mm: '',
        feeding_time_slot: 'morning',
        remarks: '',
        feed_brand: '',
        unit_cost: ''
      });
      
      await loadFeedData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add feed entry');
    } finally {
      setSubmitting(false);
    }
  };

  const navigateCalendar = (direction) => {
    let newMonth = calendarMonth + direction;
    let newYear = calendarYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setCalendarMonth(newMonth);
    setCalendarYear(newYear);
  };

  const getTimeSlotIcon = (slot) => {
    const found = TIME_SLOTS.find(s => s.value === slot);
    return found ? found.icon : '🍽️';
  };

  const getFeedTypeColor = (type) => {
    const colors = {
      starter: 'info',
      grower: 'success',
      finisher: 'primary',
      supplement: 'secondary',
      probiotic: 'warning',
      medicated: 'error'
    };
    return colors[type] || 'default';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calendar grid component
  const renderCalendar = () => {
    if (!calendar) return null;
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    // Get first day of month and number of days
    const firstDay = new Date(calendarYear, calendarMonth - 1, 1).getDay();
    const daysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
    
    // Create calendar grid
    const weeks = [];
    let days = [];
    
    // Add empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<Box key={`empty-${i}`} sx={{ p: 1, minHeight: 60 }} />);
    }
    
    // Add day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendar.days.find(d => d.date === dateStr);
      
      days.push(
        <Box 
          key={day} 
          sx={{ 
            p: 1, 
            minHeight: 70, 
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            bgcolor: dayData ? 'primary.50' : 'background.paper',
            cursor: dayData ? 'pointer' : 'default',
            '&:hover': dayData ? { bgcolor: 'primary.100' } : {}
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{day}</Typography>
          {dayData && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 600 }}>
                {dayData.total_quantity_kg} kg
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {dayData.time_slots.map(slot => (
                  <Typography key={slot} variant="caption" sx={{ fontSize: '0.65rem' }}>
                    {getTimeSlotIcon(slot)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      );
      
      if ((firstDay + day) % 7 === 0 || day === daysInMonth) {
        weeks.push(
          <Grid container spacing={0.5} key={`week-${weeks.length}`}>
            {days.map((d, i) => (
              <Grid item xs key={i} sx={{ minWidth: 0 }}>{d}</Grid>
            ))}
            {/* Fill remaining cells */}
            {day === daysInMonth && (7 - days.length) > 0 && 
              [...Array(7 - days.length)].map((_, i) => (
                <Grid item xs key={`end-${i}`} sx={{ minWidth: 0 }}>
                  <Box sx={{ p: 1, minHeight: 60 }} />
                </Grid>
              ))
            }
          </Grid>
        );
        days = [];
      }
    }
    
    return (
      <Box>
        {/* Calendar Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigateCalendar(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {monthNames[calendarMonth - 1]} {calendarYear}
          </Typography>
          <IconButton onClick={() => navigateCalendar(1)}>
            <ArrowForward />
          </IconButton>
        </Box>
        
        {/* Weekday headers */}
        <Grid container spacing={0.5} sx={{ mb: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Grid item xs key={day}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', textAlign: 'center' }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>
        
        {/* Calendar weeks */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {weeks}
        </Box>
        
        {/* Monthly total */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {calendar.total_monthly_feed_kg} kg
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Feed This Month
          </Typography>
        </Box>
      </Box>
    );
  };

  // Timeline component
  const renderTimeline = () => {
    if (feedEvents.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Restaurant sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No feed entries yet
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => setAddDialogOpen(true)}
            sx={{ mt: 2 }}
          >
            Add First Feed Entry
          </Button>
        </Box>
      );
    }
    
    // Group by date
    const groupedEvents = {};
    feedEvents.forEach(event => {
      const date = formatDate(event.feed_date_time);
      if (!groupedEvents[date]) {
        groupedEvents[date] = [];
      }
      groupedEvents[date].push(event);
    });
    
    return (
      <Box>
        {Object.entries(groupedEvents).map(([date, events]) => (
          <Box key={date} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              fontWeight: 600, 
              color: 'primary.main', 
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Today sx={{ fontSize: 18 }} />
              {date}
            </Typography>
            
            <Box sx={{ pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              {events.map(event => (
                <Paper 
                  key={event.id} 
                  sx={{ 
                    p: 2, 
                    mb: 1, 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="body2">{getTimeSlotIcon(event.feeding_time_slot)}</Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {event.quantity_kg} kg
                      </Typography>
                      <Chip 
                        label={event.feed_type} 
                        size="small" 
                        color={getFeedTypeColor(event.feed_type)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {event.doc_day && (
                        <Chip 
                          label={`DOC ${event.doc_day}`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(event.feed_date_time)} • {event.feeding_time_slot}
                      {event.feed_brand && ` • ${event.feed_brand}`}
                    </Typography>
                    {event.remarks && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        "{event.remarks}"
                      </Typography>
                    )}
                  </Box>
                  
                  {event.total_cost && (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                      ₹{event.total_cost.toFixed(0)}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Feed Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pondName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadFeedData} disabled={loading}>
            <Refresh />
          </IconButton>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Feed Entry
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Quick Indicators */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'primary.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {summary.total_feed_kg}
                </Typography>
                <Typography variant="caption" color="text.secondary">Total Feed (kg)</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'success.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {summary.average_daily_feed_kg || '--'}
                </Typography>
                <Typography variant="caption" color="text.secondary">Avg Daily (kg)</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {summary.last_feed_time ? formatDate(summary.last_feed_time) : '--'}
                </Typography>
                <Typography variant="caption" color="text.secondary">Last Feed</Typography>
                {summary.last_feed_time && (
                  <Typography variant="caption" display="block" color="primary.main">
                    {formatTime(summary.last_feed_time)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'warning.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  {summary.next_feed_time ? formatTime(summary.next_feed_time) : '--'}
                </Typography>
                <Typography variant="caption" color="text.secondary">Next Feed</Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  DOC: {summary.current_doc || '--'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<Restaurant />} label="Feed Log" iconPosition="start" />
          <Tab icon={<CalendarMonth />} label="Calendar" iconPosition="start" />
          <Tab icon={<TimelineIcon />} label="Timeline" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date/Time</strong></TableCell>
                      <TableCell><strong>Quantity</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Time Slot</strong></TableCell>
                      <TableCell><strong>DOC</strong></TableCell>
                      <TableCell><strong>Brand</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                            No feed entries yet. Click "Add Feed Entry" to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      feedEvents.map(event => (
                        <TableRow key={event.id} hover>
                          <TableCell>
                            <Typography variant="body2">{formatDate(event.feed_date_time)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(event.feed_date_time)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {event.quantity_kg} kg
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={event.feed_type} 
                              size="small" 
                              color={getFeedTypeColor(event.feed_type)}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getTimeSlotIcon(event.feeding_time_slot)} {event.feeding_time_slot}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {event.doc_day && (
                              <Chip label={`Day ${event.doc_day}`} size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {event.feed_brand || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {tabValue === 1 && renderCalendar()}
            
            {tabValue === 2 && renderTimeline()}
          </>
        )}
      </Paper>

      {/* Add Feed Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Restaurant />
            Add Feed Entry
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Feed Date & Time"
                value={formData.feed_date_time}
                onChange={(e) => setFormData({ ...formData, feed_date_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Slot</InputLabel>
                <Select
                  value={formData.feeding_time_slot}
                  label="Time Slot"
                  onChange={(e) => setFormData({ ...formData, feeding_time_slot: e.target.value })}
                >
                  {TIME_SLOTS.map(slot => (
                    <MenuItem key={slot.value} value={slot.value}>
                      {slot.icon} {slot.label} ({slot.hours})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantity (kg)"
                value={formData.quantity_kg}
                onChange={(e) => setFormData({ ...formData, quantity_kg: e.target.value })}
                required
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Feed Type</InputLabel>
                <Select
                  value={formData.feed_type}
                  label="Feed Type"
                  onChange={(e) => setFormData({ ...formData, feed_type: e.target.value })}
                >
                  {FEED_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Feed Brand (Optional)"
                value={formData.feed_brand}
                onChange={(e) => setFormData({ ...formData, feed_brand: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Feed Size (mm)"
                value={formData.feed_size_mm}
                onChange={(e) => setFormData({ ...formData, feed_size_mm: e.target.value })}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Unit Cost (₹/kg)"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                inputProps={{ step: 1, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Remarks (Optional)"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              />
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Feed entries are recorded as insurance evidence and cannot be modified after submission.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button onClick={() => setAddDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddFeed}
            disabled={submitting || !formData.quantity_kg}
            startIcon={submitting ? <CircularProgress size={20} /> : <Add />}
          >
            Add Entry
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedManagement;

